import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Button, Stack } from '@mui/material';

import FreeTextSearchFilter, {
  SEARCHBAR_BACKGROUND,
} from '../../../components/FreeTextSearchFilter';
import LabeledField from '../../../components/LabeledField';
import { SearchableSelectDropdownFilterDeprecated } from '../../../components/SearchableSelectDropdownFilter/SearchableSelectDropdownFilter';
import UserSearchOption, {
  USER_SEARCH_DROPDOWN_MIN_WIDTH,
} from '../../../components/UserSearchOption/UserSearchOption';
import {
  agencyAPICallback,
  corporationsAPICallback,
  departmentAPICallback,
  employeeAPICallback,
  locationsAPICallback,
} from '../timekeepingV2ApiCallbacks';

const TimekeepingGlobalFilters = ({
  isTimecardsLoading,
  isUserAdminOrRecruiter,
  selectedEmployeeId,
  selectedCorporationId,
  selectedAgencyId,
  selectedDepartmentId,
  selectedLocationId,
  corporationInitialValue,
  positionInitialValue,
  handleEmployeeSearchChange,
  handleCorporationChange,
  handleAgencyChange,
  handleDepartmentChange,
  handleLocationChange,
  handlePositionSearchChange,
  clearGlobalFilters,
}) => (
  <Stack gap="12px">
    <Box display="flex" flexDirection="row" gap="12px" flexWrap="wrap" alignItems="flex-end">
      <LabeledField
        title={LanguageConverterV2('timekeeping.filter.employeeName')}
        field={
          <SearchableSelectDropdownFilterDeprecated
            disabled={isTimecardsLoading}
            placeholder={LanguageConverterV2('timekeeping.filter.employeeName')}
            onValueChange={handleEmployeeSearchChange}
            optionsAPICallback={employeeAPICallback(selectedEmployeeId)}
            getOptionLabel={(option) => option.name}
            renderOptionContent={(option) => <UserSearchOption user={option} />}
            dropdownMinWidth={USER_SEARCH_DROPDOWN_MIN_WIDTH}
            background={SEARCHBAR_BACKGROUND.DEFAULT}
            initialValue={selectedEmployeeId}
          />
        }
      />
      {isUserAdminOrRecruiter && (
        <LabeledField
          title={LanguageConverterV2('timekeeping.filter.corporation')}
          field={
            <SearchableSelectDropdownFilterDeprecated
              disabled={isTimecardsLoading}
              placeholder={LanguageConverterV2('timekeeping.filter.corporation')}
              onValueChange={handleCorporationChange}
              optionsAPICallback={corporationsAPICallback(selectedCorporationId)}
              getOptionLabel={(option) => option.name}
              background={SEARCHBAR_BACKGROUND.DEFAULT}
              initialValue={corporationInitialValue}
            />
          }
        />
      )}
      <LabeledField
        title={LanguageConverterV2('timekeeping.filter.agency')}
        field={
          <SearchableSelectDropdownFilterDeprecated
            disabled={isTimecardsLoading}
            placeholder={LanguageConverterV2('timekeeping.filter.agency')}
            onValueChange={handleAgencyChange}
            optionsAPICallback={agencyAPICallback(selectedAgencyId)}
            getOptionLabel={(option) => option.name}
            background={SEARCHBAR_BACKGROUND.DEFAULT}
            initialValue={selectedAgencyId}
          />
        }
      />
      <LabeledField
        title={LanguageConverterV2('timekeeping.filter.department')}
        field={
          <SearchableSelectDropdownFilterDeprecated
            disabled={isTimecardsLoading || !selectedCorporationId}
            placeholder={LanguageConverterV2('timekeeping.filter.department')}
            onValueChange={handleDepartmentChange}
            optionsAPICallback={departmentAPICallback(selectedCorporationId, selectedDepartmentId)}
            getOptionLabel={(option) => option.name}
            background={SEARCHBAR_BACKGROUND.DEFAULT}
            initialValue={selectedDepartmentId}
          />
        }
      />
      <LabeledField
        title={LanguageConverterV2('timekeeping.filter.location')}
        field={
          <SearchableSelectDropdownFilterDeprecated
            disabled={isTimecardsLoading || !selectedCorporationId}
            placeholder={LanguageConverterV2('timekeeping.filter.location')}
            onValueChange={handleLocationChange}
            optionsAPICallback={locationsAPICallback(selectedCorporationId, selectedLocationId)}
            getOptionLabel={(option) => `${option.address} - ${option.description}`}
            background={SEARCHBAR_BACKGROUND.DEFAULT}
            initialValue={selectedLocationId}
          />
        }
      />
      <LabeledField
        title={LanguageConverterV2('timekeeping.filter.positionName')}
        field={
          <FreeTextSearchFilter
            placeholder={LanguageConverterV2('timekeeping.filter.positionName')}
            onValueChange={handlePositionSearchChange}
            field="name"
            operation="icontains"
            background={SEARCHBAR_BACKGROUND.DEFAULT}
            initialValue={{ value: positionInitialValue }}
            wrapperSx={{ opacity: isTimecardsLoading ? 0.5 : 1 }}
          />
        }
      />
      <Button
        variant="contained"
        onClick={clearGlobalFilters}
        disabled={isTimecardsLoading}
        sx={{ height: '35px', textTransform: 'none' }}
      >
        {LanguageConverterV2('timekeeping.filter.clearAll')}
      </Button>
    </Box>
  </Stack>
);

export default TimekeepingGlobalFilters;

TimekeepingGlobalFilters.propTypes = {
  isTimecardsLoading: PropTypes.bool,
  isUserAdminOrRecruiter: PropTypes.bool,
  selectedEmployeeId: PropTypes.string,
  selectedCorporationId: PropTypes.string,
  selectedAgencyId: PropTypes.string,
  selectedDepartmentId: PropTypes.string,
  selectedLocationId: PropTypes.string,
  corporationInitialValue: PropTypes.string,
  positionInitialValue: PropTypes.string,
  handleEmployeeSearchChange: PropTypes.func.isRequired,
  handleCorporationChange: PropTypes.func.isRequired,
  handleAgencyChange: PropTypes.func.isRequired,
  handleDepartmentChange: PropTypes.func.isRequired,
  handleLocationChange: PropTypes.func.isRequired,
  handlePositionSearchChange: PropTypes.func.isRequired,
  clearGlobalFilters: PropTypes.func.isRequired,
};

TimekeepingGlobalFilters.defaultProps = {
  isTimecardsLoading: false,
  isUserAdminOrRecruiter: false,
  selectedEmployeeId: undefined,
  selectedCorporationId: undefined,
  selectedAgencyId: undefined,
  selectedDepartmentId: undefined,
  selectedLocationId: undefined,
  corporationInitialValue: undefined,
  positionInitialValue: undefined,
};
